// Pure transform from a target-day-forecasts payload into props for the
// forecast chart. Kept separate from the panel so the role-aware shaping is
// unit-testable without rendering the chart.
//
// Payload shape: { ensemble: [{datetime,value,variable}], forecasts: { uid:
//   { username, email?, data: [{datetime,value}] } }, start_datetime, end_datetime }

export const hasForecastsData = (targetDayForecasts, sessionRawData = []) => {
  const ensemble = targetDayForecasts?.ensemble || [];
  const forecastsMap = targetDayForecasts?.forecasts || {};
  return (
    ensemble.length > 0 ||
    Object.keys(forecastsMap).length > 0 ||
    (sessionRawData?.length || 0) > 0
  );
};

export const buildForecastsChartProps = (
  targetDayForecasts,
  sessionRawData = [],
  canViewOthersData = false,
  blurSensitive = false,
  // null = all forecasters; an array limits the individual lines to those uids.
  selectedForecasterIds = null
) => {
  const ensemble = targetDayForecasts?.ensemble || [];
  const forecastsMap = targetDayForecasts?.forecasts || {};

  // Per-forecaster series. Only superusers receive `email`; show it to them
  // unless sensitive info is hidden, otherwise fall back to the username alias.
  let individualUserForecasts = null;
  if (Object.keys(forecastsMap).length > 0) {
    individualUserForecasts = {};
    for (const [uid, userData] of Object.entries(forecastsMap)) {
      if (Array.isArray(selectedForecasterIds) && !selectedForecasterIds.includes(uid)) continue;
      individualUserForecasts[uid] = {
        ...userData,
        userName: !blurSensitive && userData.email ? userData.email : userData.username || uid,
      };
    }
  }

  // Forecasters never get the ensemble (backend already omits it). Show their
  // own submission as the main forecast series instead, keeping the q10/q50/q90
  // variables so the chart draws their interval band (q50 line + q10–q90 band),
  // mirroring the Homepage forecaster view. Rows without a variable (legacy /
  // q50-only submissions) default to q50 so the main line still draws.
  let ensembleForecast = ensemble;
  if (!canViewOthersData && individualUserForecasts) {
    const entries = Object.values(individualUserForecasts);
    if (entries.length > 0) {
      ensembleForecast = entries[0].data.map((item) => ({
        ...item,
        variable: item.variable || 'q50',
      }));
    }
    individualUserForecasts = null;
  }

  return {
    ensembleForecast,
    individualUserForecasts,
    rawData: sessionRawData ?? [],
    timeUnit: 'hour',
    isForecaster: !canViewOthersData,
    // Draw individual forecaster lines for anyone allowed to see others' data
    // (market owners / admins). Market owners always have `blurSensitive` on,
    // so gating on it hid their lines entirely; identities are instead protected
    // by the alias labels + tooltip anonymisation in the chart, not by hiding.
    showUserLegend: canViewOthersData,
    challengeStart: targetDayForecasts?.start_datetime || null,
    challengeEnd: targetDayForecasts?.end_datetime || null,
  };
};
